import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { v4 as uuid } from "uuid";
import Select from "react-select";
import { fromBlob } from "geotiff";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import SweetAlert from "react-bootstrap-sweetalert";
import tmdbApi from "../../../api/tmdbApi";
import Header from "../../../components/dashboard/header";
import Footer from "../../../components/dashboard/footer";
import SideMenu from "../../student/SideMenu";
import Loader from "../../../components/loader";
import SessionPopup from "../../SessionPopup";
import { contentContext } from "../../../context/contentContext";
import { Uploader } from "../../../utils/upload";
import progessIcon from "../../../assets/images/gifs/process-icon.gif";
import loadingIcon from "../../../assets/images/gifs/uploading-icon.gif";
import deepZoom from "../../../assets/images/icons/Dna-dz.svg";

let { lambda, appname } = window.app;

const MAX_PIXELS = 10_000_000_000;
const UPLOADER_BASE_URL = "https://qih3mm5tl8.execute-api.ap-south-1.amazonaws.com/prod/";

function getExtension(filename) {
    if (!filename) return null;
    const ext = filename.split(".").pop().toLowerCase();
    return ext === "tiff" || ext === "tif" ? ".tif,.tiff" : ".jpeg,.jpg,.png";
}

async function getFileDimensions(file) {
    const name = file.name?.toLowerCase() || "";
    if (name.endsWith(".tif") || name.endsWith(".tiff")) {
        const tiff = await fromBlob(file);
        const image = await tiff.getImage();
        return { width: image.getWidth(), height: image.getHeight() };
    }
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const img = new Image();
        img.onload = () => { URL.revokeObjectURL(url); resolve({ width: img.width, height: img.height }); };
        img.onerror = reject;
        img.src = url;
    });
}

export default function LabBiopsyCreate() {
    const navigate = useNavigate();
    const labUserId = localStorage.getItem("userId");
    const token = localStorage.getItem("token");

    const {
        route, setRoute,
        setCurrentPage, setRowsPerPage,
        setActiveMenuObj, setActiveMenuId,
        usePrevious,
        GetTimeActivity,
        setIsUploading, setIsUploadingFailed,
    } = useContext(contentContext);

    // ── Form state ─────────────────────────────────────────────────────────
    const [activeTab, setActiveTab] = useState("summary");
    const [caseData, setCaseData] = useState({
        pathrefno: "",
        biopsydate: "",
        patientname: "",
        age: "",
        gender: "",
        clinicregistrationnumber: "",
        specimentype: "",
        siteofbiopsy: "",
        clinicalhistory: "",
    });
    const [selectedBiopsyType, setSelectedBiopsyType] = useState(null);
    const [biopsyTypeOptions, setBiopsyTypeOptions] = useState([]);
    const [defaultTestName, setDefaultTestName] = useState("Biopsy");
    const [defaultTestCost, setDefaultTestCost] = useState("1000");

    // ── Clinical image state ───────────────────────────────────────────────
    const [clinicalImageFiles, setClinicalImageFiles] = useState([]);
    const [clinicalImageUrls, setClinicalImageUrls] = useState(null);
    const [clinicalImageIndex, setClinicalImageIndex] = useState(0);
    const [imageCloudfront, setImageCloudfront] = useState("");
    const [resourceBucket, setResourceBucket] = useState("");
    const [showImagePopup, setShowImagePopup] = useState(false);
    const [popupImageUrl, setPopupImageUrl] = useState(null);
    const [fileSizeError, setFileSizeError] = useState(false);
    const [fileSizeErrorMsg, setFileSizeErrorMsg] = useState("");

    // ── histology image state ───────────────────────────────────────────────
    const [histologyImageFiles, setHistologyImageFiles] = useState([]);
    const [histologyImageUrls, setHistologyImageUrls] = useState(null);
    const [histologyImageIndex, setHistologyImageIndex] = useState(0);
    const [fileDimension, setFileDimension] = useState({});

    // ── pathologist assignment ──────────────────────────────────────────────
    const [pathologistList, setPathologistList] = useState([]);
    const [selectedPathologistId, setSelectedPathologistId] = useState("");

    // ── dermatologist ────────────────────────────────────────────────────────
    const [dermatologistList, setDermatologistList] = useState([]);
    const [selectedDermatologist, setSelectedDermatologist] = useState(null);

    // ── payment ──────────────────────────────────────────────────────────────
    const [paymentMode, setPaymentMode] = useState("");
    const [paymentDate, setPaymentDate] = useState("");
    const [paymentId, setPaymentId] = useState("");

    // ── UI state ───────────────────────────────────────────────────────────
    const [isLoading, setIsLoading] = useState(false);
    const [showSessionPopup, setShowSessionPopup] = useState(false);
    const [successPopup, setSuccessPopup] = useState(false);
    const [createdPatientId, setCreatedPatientId] = useState(null);

    // ── Validation errors ──────────────────────────────────────────────────
    const [errors, setErrors] = useState({});

    const prevRoute = usePrevious(route);
    useEffect(() => {
        if (prevRoute !== undefined && prevRoute !== route) {
            setCurrentPage(1);
            setRowsPerPage(15);
        }
    }, [prevRoute]);

    useEffect(() => {
        if (window.site?.common?.imageCloudfront) {
            setImageCloudfront(window.site.common.imageCloudfront.replace(/^http:\/\//i, "https://"));
            setResourceBucket(window.site.common.resourceBucket);
        } else {
            setTimeout(() => {
                if (window.site?.common?.imageCloudfront) {
                    setImageCloudfront(window.site.common.imageCloudfront.replace(/^http:\/\//i, "https://"));
                    setResourceBucket(window.site.common.resourceBucket);
                }
            }, 1000);
        }
    }, [window.site]);

    useEffect(() => {
        if (!token) navigate("/");
        setActiveMenuId(200005);
        setActiveMenuObj({ "Client Management": false, "Contact Management": false, "Reports": false });
        setRoute("user");
        fetchLookupOptions("Biopsy");
        fetchAutoPathRefNo();
        fetchPathologists();
        fetchDermatologists();
    }, []);

    const fetchLookupOptions = async (type) => {
        const res = await tmdbApi.lookupInfo(type);
        if (res?.result?.length > 0) {
            if (type === "Biopsy") setBiopsyTypeOptions(res.result);
        }
    };

    const fetchAutoPathRefNo = async () => {
        const res = await tmdbApi.getBiopsyRefNumber();
        if (res?.result?.biopsyrefid) {
            setCaseData((prev) => ({ ...prev, pathrefno: res.result.biopsyrefid }));
        }
    };

    const fetchPathologists = async () => {
        try {
            const res = await axios.get(`${lambda}/userInfo?appname=${appname}&userType=PATHOLOGIST&token=${token}`);
            if (res?.data?.result && Array.isArray(res.data.result)) {
                setPathologistList(res.data.result);
            }
        } catch (err) {
            console.error("fetchPathologists error:", err);
        }
    };

    const fetchDermatologists = async () => {
        try {
            const [doctorRes, adminRes] = await Promise.all([
                axios.get(`${lambda}/userInfo?appname=${appname}&userType=DOCTOR&token=${token}`),
                axios.get(`${lambda}/userInfo?appname=${appname}&userType=ADMIN&token=${token}`),
            ]);
            const doctors = Array.isArray(doctorRes?.data?.result) ? doctorRes.data.result : [];
            const admins = Array.isArray(adminRes?.data?.result) ? adminRes.data.result : [];
            setDermatologistList([...doctors, ...admins]);
        } catch (err) {
            console.error("fetchDermatologists error:", err);
        }
    };

    const handleFieldChange = (e) => {
        const { name, value } = e.target;
        setCaseData((prev) => ({ ...prev, [name]: value }));
    };

    const handleBiopsyTypeChange = (selected) => {
        setSelectedBiopsyType(selected);
        setCaseData((prev) => ({ ...prev, biopsyType: selected }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!caseData.pathrefno?.trim()) newErrors.pathrefno = "Please enter Path Ref No";
        if (!caseData.biopsydate?.trim()) newErrors.biopsydate = "Please enter Biopsy Date";
        if (!caseData.patientname?.trim()) newErrors.patientname = "Please enter Patient Name";
        if (!caseData.clinicregistrationnumber?.trim()) newErrors.clinicregistrationnumber = "Please enter Clinic Registration Number";
        if (!caseData.specimentype?.trim()) newErrors.specimentype = "Please enter Specimen Type";
        if (!caseData.siteofbiopsy?.trim()) newErrors.siteofbiopsy = "Please enter Site of Biopsy";
        if (!caseData.clinicalhistory?.trim()) newErrors.clinicalhistory = "Please enter Clinical History";
        if (!selectedBiopsyType || (Array.isArray(selectedBiopsyType) && selectedBiopsyType.length === 0)) {
            newErrors.biopsyType = "Please select Biopsy Type";
        }
        setErrors(newErrors);
        setTimeout(() => setErrors({}), 3000);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        GetTimeActivity();
        if (!validateForm()) return;
        createCase();
    };

    const createCase = async () => {
        setIsLoading(true);
        try {
            const patientId = uuid();
            setCreatedPatientId(patientId);

            const biopsyfiles = clinicalImageFiles.length > 0
                ? await uploadClinicalImages(clinicalImageFiles, patientId)
                : [];
            const histologyImages = histologyImageFiles.length > 0
                ? await uploadHistologyImages(histologyImageFiles, patientId)
                : [];

            const selectedPathologist = pathologistList.find((p) => p.userid === selectedPathologistId);
            const hasPayment = !!(paymentMode && paymentDate && paymentId.trim());

            const payload = {
                ...caseData,
                biopsyType: selectedBiopsyType,
                patientid: patientId,
                caseType: "Biopsy",
                status: hasPayment ? "Payment Received" : (selectedPathologistId ? "Assigned to Pathologist" : "Submitted"),
                createdby: labUserId,
                biopsyfiles,
                histologyImages,
                defaultBiopsyTest: {
                    name: defaultTestName || "Biopsy Test",
                    cost: defaultTestCost,
                    isDefault: true,
                },
            };
            if (selectedPathologistId) {
                payload.assignedPathologist = selectedPathologistId;
                payload.assignedPathologistName = selectedPathologist?.name || "";
            }
            if (selectedDermatologist) {
                payload.dermatologistId = selectedDermatologist.value;
                payload.dermatologistName = selectedDermatologist.label;
            }
            if (hasPayment) {
                payload.paymentInfo = {
                    testName: defaultTestName || "Biopsy",
                    amount: defaultTestCost || "1000",
                    paymentMode,
                    paymentDate,
                    paymentId: paymentId.trim(),
                    confirmedAt: new Date().toISOString(),
                    confirmedBy: labUserId,
                };
            }

            const res = await axios.post(`${lambda}/addBiopsyCase?appname=${appname}&userid=${labUserId}`, payload);
            if (res?.data?.result === "Invalid token or Expired") {
                setShowSessionPopup(true);
            } else {
                setSuccessPopup(true);
            }
        } catch (err) {
            console.error("createCase error:", err);
            toast.error("Failed to create case. Please try again.");
        } finally {
            setIsLoading(false);
        }
    };

    // ── clinical image upload ────────────────────────────────────────────────

    const uploadClinicalImages = async (files, biopsyId) => {
        const bucket = resourceBucket || window.site?.common?.resourceBucket;
        const uploadedFiles = await Promise.all(
            Array.from(files).map(async (file) => {
                const timestamp = Date.now();
                const ext = file.name.split(".").pop();
                const ObjectID = require("bson-objectid");
                const uniqueId = new ObjectID().toString();
                const filename = `${uniqueId}_image_${timestamp}.${ext}`;
                const uploadPath = `${appname}/${biopsyId}/${uniqueId}/${filename}`;
                const data = { source_bucket: bucket, sourcepath: uploadPath };
                try {
                    const signedRes = await axios.post(
                        `${lambda}/uploadFiles?appname=${appname}&biopsyid=${biopsyId}`,
                        data,
                        { headers: { "Content-Type": "application/json" } }
                    );
                    if (signedRes?.data?.result) {
                        await axios.put(signedRes.data.result, file, {
                            headers: { "Content-Type": file.type, "Cache-Control": "no-cache" },
                        });
                        return { fileid: uniqueId, filename, sourcepath: uploadPath };
                    }
                    return null;
                } catch (err) {
                    console.error("Image upload error:", err);
                    return null;
                }
            })
        );
        return uploadedFiles.filter(Boolean);
    };

    const handleClinicalImageSelect = (e) => {
        const selectedFiles = Array.from(e.target.files);
        const maxSize = 1048612 * 20;
        selectedFiles.forEach((file, index) => {
            if (file.size < maxSize) {
                const adjustedIndex = clinicalImageIndex + index;
                const blobUrl = URL.createObjectURL(file);
                const img = new Image();
                img.onload = () => {
                    setClinicalImageFiles((prev) => [...prev, file]);
                    setClinicalImageUrls((prev) => ({
                        ...prev,
                        [`blob${adjustedIndex}`]: { url: blobUrl, name: file.name },
                    }));
                    setClinicalImageIndex(adjustedIndex + 1);
                };
                img.src = blobUrl;
            } else {
                setFileSizeError(true);
                setTimeout(() => setFileSizeError(false), 3000);
            }
        });
    };

    const handleClinicalImageRemove = (key) => {
        const urls = { ...clinicalImageUrls };
        setClinicalImageFiles(clinicalImageFiles.filter((f) => f.name !== urls[key]?.name));
        delete urls[key];
        setClinicalImageUrls(urls);
        setClinicalImageIndex(clinicalImageIndex - 1);
    };

    // ── histology image upload ───────────────────────────────────────────────

    const handleHistologyFileChange = async (e) => {
        const selectedFiles = Array.from(e.target.files);
        e.target.value = "";

        for (const file of selectedFiles) {
            try {
                const dims = await getFileDimensions(file);
                if (dims.width * dims.height > MAX_PIXELS) {
                    setFileSizeError(true);
                    setFileSizeErrorMsg(`"${file.name}" resolution exceeds 10 billion pixels.`);
                    continue;
                }
                setFileDimension((prev) => ({ ...prev, [file.name]: dims }));

                const urlobj = URL.createObjectURL(file);
                const adjustedIndex = histologyImageIndex;
                setHistologyImageFiles((prev) => [...prev, file]);
                setHistologyImageUrls((prev) => ({
                    ...prev,
                    [`blob${adjustedIndex}`]: { url: urlobj, name: file.name },
                }));
                setHistologyImageIndex((prev) => prev + 1);
            } catch (err) {
                console.error("Dimension read error:", err);
            }
        }
    };

    const handleHistologyImageRemove = (key) => {
        const name = histologyImageUrls?.[key]?.name;
        setHistologyImageFiles((prev) => prev.filter((f) => f.name !== name));
        setHistologyImageUrls((prev) => {
            if (!prev) return prev;
            const u = { ...prev };
            delete u[key];
            return u;
        });
    };

    // uploads every selected histology file and resolves once ALL have finished
    // (success or failure), so case creation can include the final array directly.
    const uploadHistologyImages = (files, patientId) => {
        const bucket = resourceBucket || window.site?.common?.resourceBucket;

        const uploadPromises = files.map((file) => new Promise((resolve) => {
            const timestamp = Date.now();
            const ObjectID = require("bson-objectid");
            const uniqueId = new ObjectID().toString();
            const fileExtension = file.name.split(".").pop();
            const fileNameUnique = `${uniqueId}_image_${timestamp}.${fileExtension}`;
            const isTiff = file.type?.toLowerCase().includes("tiff") || file.type?.toLowerCase().includes("tif");
            const isLarge = file.size > 8 * 1024 * 1024;

            const uploadFilePath = (isTiff || isLarge)
                ? `${appname}/cases/histology/${patientId}/${uniqueId}/`
                : `${appname}/cases/histology-jpg/${patientId}/${uniqueId}/`;

            let newFile = {
                fileCreated: new Date(),
                filename: fileNameUnique,
                filetype: file.type,
                sourcepath: uploadFilePath + fileNameUnique,
                fileid: uniqueId,
                originalFilename: file.name,
                fileSize: file.size,
                width: fileDimension?.[file.name]?.width || 0,
                height: fileDimension?.[file.name]?.height || 0,
                resolution: (fileDimension?.[file.name]?.width || 0) * (fileDimension?.[file.name]?.height || 0),
            };
            if (isTiff || isLarge) {
                newFile.allow = true;
                newFile.allowDziCreation = true;
            }
            if (!isLarge && (file.type === "image/jpeg" || file.type === "image/jpg" || file.type === "image/png")) {
                newFile.dzistatus = "COMPLETED";
            }

            const uploader = new Uploader({
                file,
                baseURL: UPLOADER_BASE_URL,
                chunkSize: 100,
                threadsQuantity: 3,
                useTransferAcceleration: undefined,
                bucket: uploadFilePath,
                fileNameUnique,
                sourceBucket: bucket,
                setIsUploading,
                setIsUploadingFailed,
            });
            uploader.onComplete(() => resolve({ ...newFile, uploadStatus: "COMPLETED" }));
            uploader.onError(() => resolve({ ...newFile, uploadStatus: "FAILED" }));
            uploader.start();
        }));

        return Promise.all(uploadPromises);
    };

    const handleImagePopup = (imageUrl) => {
        setShowImagePopup(true);
        setPopupImageUrl(imageUrl);
    };

    const handleBack = () => {
        GetTimeActivity();
        navigate("/biopsy/cases");
    };

    const onCreateSuccess = () => {
        setSuccessPopup(false);
        navigate(`/biopsy/cases`);
    };

    return (
        <>
            {showSessionPopup && <SessionPopup />}
            <ToastContainer position="bottom-center" autoClose={3000} />

            <SweetAlert
                show={successPopup}
                custom
                confirmBtnText="Ok"
                confirmBtnBsStyle="primary"
                title="Case created successfully"
                onConfirm={onCreateSuccess}
            />

            <div id="layout-wrapper">
                <Header />
                <SideMenu />

                <div className="main-content user-management create-user" id="main-content">
                    <div className="page-content">
                        <div className="container-fluid">
                            <div className="row mb-4 breadcrumb">
                                <div className="col-lg-12">
                                    <div className="d-flex align-items-center">
                                        <div className="flex-grow-1">
                                            <h4 className="mb-2 card-title">CREATE NEW CASE</h4>
                                            <p className="menu-path">
                                                <span>BIOPSY</span> / <b>CREATE</b>
                                            </p>
                                        </div>
                                        <a onClick={handleBack} className="create-quiz-btn border-0" style={{ cursor: "pointer" }}>
                                            Back
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-body p-0">
                                            <ul className="nav nav-tabs" style={{ borderBottom: "1px solid #dee2e6", padding: "0 16px" }}>
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link ${activeTab === "summary" ? "active" : ""}`}
                                                        onClick={() => setActiveTab("summary")}
                                                    >
                                                        Patient Summary
                                                    </button>
                                                </li>
                                                <li className="nav-item">
                                                    <button
                                                        className={`nav-link ${activeTab === "payment" ? "active" : ""}`}
                                                        onClick={() => setActiveTab("payment")}
                                                    >
                                                        Payment
                                                    </button>
                                                </li>
                                            </ul>

                                            <div className="p-4">
                                                {isLoading ? <Loader /> : (
                                                    <>
                                                        {/* ════ PATIENT SUMMARY TAB ════ */}
                                                        {activeTab === "summary" && (
                                                            <div className="row col-lg-12">

                                                                {/* PATH REF NO */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">PATH REF NO <span className="required">*</span></label>
                                                                        <input
                                                                            name="pathrefno"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Path Ref No"
                                                                            value={caseData.pathrefno}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.pathrefno && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.pathrefno}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* BIOPSY DATE */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">BIOPSY DATE <span className="required">*</span></label>
                                                                        <input
                                                                            name="biopsydate"
                                                                            type="date"
                                                                            className="form-control"
                                                                            value={caseData.biopsydate}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.biopsydate && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.biopsydate}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* CLINIC REGISTRATION NUMBER */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">CLINIC REGISTRATION NUMBER <span className="required">*</span></label>
                                                                        <input
                                                                            name="clinicregistrationnumber"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Clinic Registration Number"
                                                                            value={caseData.clinicregistrationnumber}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.clinicregistrationnumber && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.clinicregistrationnumber}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* PATIENT NAME */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">PATIENT NAME <span className="required">*</span></label>
                                                                        <input
                                                                            name="patientname"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Patient Name"
                                                                            value={caseData.patientname}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.patientname && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.patientname}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* AGE */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">AGE</label>
                                                                        <input
                                                                            name="age"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Age"
                                                                            value={caseData.age}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* GENDER */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">GENDER</label>
                                                                        <select
                                                                            name="gender"
                                                                            className="form-control form-select"
                                                                            value={caseData.gender}
                                                                            onChange={handleFieldChange}
                                                                        >
                                                                            <option value="">Select</option>
                                                                            <option value="Male">Male</option>
                                                                            <option value="Female">Female</option>
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                {/* SPECIMEN TYPE */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">SPECIMEN TYPE <span className="required">*</span></label>
                                                                        <input
                                                                            name="specimentype"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Specimen Type"
                                                                            value={caseData.specimentype}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.specimentype && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.specimentype}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* SITE OF BIOPSY */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">SITE OF BIOPSY <span className="required">*</span></label>
                                                                        <input
                                                                            name="siteofbiopsy"
                                                                            type="text"
                                                                            className="form-control"
                                                                            placeholder="Enter Site of Biopsy"
                                                                            value={caseData.siteofbiopsy}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.siteofbiopsy && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.siteofbiopsy}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* BIOPSY TYPE */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">BIOPSY TYPE <span className="required">*</span></label>
                                                                        <Select
                                                                            isMulti={false}
                                                                            placeholder="Select Biopsy Type"
                                                                            value={selectedBiopsyType}
                                                                            onChange={handleBiopsyTypeChange}
                                                                            options={biopsyTypeOptions.map((item) => ({
                                                                                value: item.lookupcode,
                                                                                label: item.name,
                                                                            }))}
                                                                            isClearable
                                                                        />
                                                                        {errors.biopsyType && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.biopsyType}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* SELECT DERMATOLOGIST */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">SELECT DERMATOLOGIST</label>
                                                                        <Select
                                                                            isMulti={false}
                                                                            placeholder="Select Dermatologist"
                                                                            value={selectedDermatologist}
                                                                            onChange={(selected) => setSelectedDermatologist(selected)}
                                                                            options={dermatologistList.map((d) => ({
                                                                                value: d.userid,
                                                                                label: d.name,
                                                                            }))}
                                                                            isClearable
                                                                        />
                                                                    </div>
                                                                </div>

                                                                {/* TESTS / SLIDES — read-only */}
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">TEST NAME</label>
                                                                        <input type="text" className="form-control" readOnly value={defaultTestName} />
                                                                    </div>
                                                                </div>
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">COST (₹)</label>
                                                                        <input type="text" className="form-control" readOnly value={defaultTestCost} />
                                                                    </div>
                                                                </div>

                                                                {/* CLINICAL HISTORY */}
                                                                <div className="col-lg-12 col-md-12 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <label className="form_label">CLINICAL HISTORY <span className="required">*</span></label>
                                                                        <textarea
                                                                            name="clinicalhistory"
                                                                            className="form-control edit-cpc-textarea"
                                                                            placeholder="Enter Clinical History"
                                                                            rows={12}
                                                                            style={{ height: "200px" }}
                                                                            cols={6}
                                                                            value={caseData.clinicalhistory}
                                                                            onChange={handleFieldChange}
                                                                        />
                                                                        {errors.clinicalhistory && <span className="errormsg" style={{ fontWeight: "bold", color: "red" }}>{errors.clinicalhistory}</span>}
                                                                    </div>
                                                                </div>

                                                                {/* ── CLINICAL HISTORY PHOTOS ─────── */}
                                                                <div className="col-md-12 mt-2">
                                                                    <label className="form_label col-12">CLINICAL HISTORY PHOTOS</label>
                                                                    <p className="mt-0 mb-0 text-muted col-8 file-format">Note: File size: 20 MB | Supported Formats: JPG, PNG</p>
                                                                    {fileSizeError && <p className="text-danger small">{fileSizeErrorMsg || "File size exceeds 20MB limit."}</p>}
                                                                    <div className="d-flex justify-content-start col-lg-12 clinical-photo-section gap-3 mt-3">
                                                                        <div className="uploaded_images-grid mb-3 mt-2">
                                                                            <div className="upload-box">
                                                                                <div className="file_upload add-box">
                                                                                    <input
                                                                                        type="file"
                                                                                        id="clinical-image-upload"
                                                                                        accept="image/jpeg, image/png"
                                                                                        multiple
                                                                                        className="file-input"
                                                                                        onChange={handleClinicalImageSelect}
                                                                                        style={{ display: "none" }}
                                                                                    />
                                                                                    <label htmlFor="clinical-image-upload" className="Add-btn d-flex align-items-center justify-content-center flex-column">
                                                                                        <p className="mb-0"><span className="material-symbols-outlined plus-icon">add</span></p>
                                                                                        ADD
                                                                                    </label>
                                                                                </div>
                                                                            </div>

                                                                            {clinicalImageUrls && Object.keys(clinicalImageUrls).map((key) => (
                                                                                <div className="thumb-box" key={key}>
                                                                                    <div
                                                                                        className="thumb-img cursor-pointer"
                                                                                        onClick={() => handleImagePopup(clinicalImageUrls[key].url)}
                                                                                    >
                                                                                        <img src={clinicalImageUrls[key].url} alt="clinical" />
                                                                                        <span
                                                                                            className="icon-close"
                                                                                            onClick={(e) => { e.stopPropagation(); handleClinicalImageRemove(key); }}
                                                                                        >×</span>
                                                                                    </div>
                                                                                    <p className="thumb-name text-truncate small mb-0">{clinicalImageUrls[key].name}</p>
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* ── HISTOLOGY PHOTOS ────────────── */}
                                                                <div className="col-md-12 mt-2">
                                                                    <label className="form_label col-12">HISTOLOGY PHOTOS</label>

                                                                    {/* JPEG */}
                                                                    <div className="d-flex justify-content-between histology-photo-section">
                                                                        <p className="case-type-text">PNG, JPEG, JPG Files Only</p>
                                                                        <p className="mt-0 mb-0 text-muted file-format">Note: Max File size: 20 MB, Max files: 20 | Formats: PNG, JPEG, JPG</p>
                                                                    </div>
                                                                    <div className="d-flex justify-content-start col-lg-12 histology-photo-section gap-2">
                                                                        <div className="mb-1 mt-2 input-field deep-zoom-styles">
                                                                            <div className="upload-image">
                                                                                <div className="file_upload add-box">
                                                                                    <input
                                                                                        type="file"
                                                                                        id="histology-jpeg-upload"
                                                                                        accept=".jpeg,.jpg,.png"
                                                                                        multiple
                                                                                        className="file-input"
                                                                                        onChange={handleHistologyFileChange}
                                                                                        style={{ display: "none" }}
                                                                                    />
                                                                                    <label htmlFor="histology-jpeg-upload" className="Add-btn d-flex align-items-center justify-content-center flex-column">
                                                                                        <p className="mb-0"><span className="material-symbols-outlined plus-icon">add</span></p>
                                                                                        ADD
                                                                                    </label>
                                                                                </div>
                                                                            </div>

                                                                            {histologyImageUrls && Object.keys(histologyImageUrls)
                                                                                .filter((k) => getExtension(histologyImageUrls[k].name) === ".jpeg,.jpg,.png")
                                                                                .map((key) => (
                                                                                    <div className="thumb-box" key={key}>
                                                                                        <div className="thumb-img">
                                                                                            <img src={deepZoom} alt="histology" />
                                                                                            <span className="icon-close" onClick={() => handleHistologyImageRemove(key)}>×</span>
                                                                                        </div>
                                                                                        <p className="thumb-name text-truncate small mb-0">{histologyImageUrls[key].name}</p>
                                                                                    </div>
                                                                                ))}
                                                                        </div>
                                                                    </div>

                                                                    {/* TIFF */}
                                                                    <div className="row col-lg-12 mt-0">
                                                                        <div className="d-flex justify-content-between tiff-files-section">
                                                                            <p className="case-type-text">TIFF Files</p>
                                                                            <p className="mt-0 mb-0 text-muted file-format">Note: Max File size: 2GB, Max files: 10 | Formats: TIF, TIFF</p>
                                                                        </div>
                                                                        <div className="d-flex justify-content-start col-lg-12 histology-photo-section gap-2">
                                                                            <div className="mb-3 mt-2 input-field deep-zoom-styles">
                                                                                <div className="upload-image">
                                                                                    <div className="file_upload add-box">
                                                                                        <input
                                                                                            type="file"
                                                                                            id="histology-tiff-upload"
                                                                                            accept=".tif,.tiff"
                                                                                            multiple
                                                                                            className="file-input"
                                                                                            onChange={handleHistologyFileChange}
                                                                                            style={{ display: "none" }}
                                                                                        />
                                                                                        <label htmlFor="histology-tiff-upload" className="Add-btn d-flex align-items-center justify-content-center flex-column">
                                                                                            <p className="mb-0"><span className="material-symbols-outlined plus-icon">add</span></p>
                                                                                            ADD
                                                                                        </label>
                                                                                    </div>
                                                                                </div>

                                                                                {histologyImageUrls && Object.keys(histologyImageUrls)
                                                                                    .filter((k) => getExtension(histologyImageUrls[k].name) === ".tif,.tiff")
                                                                                    .map((key) => (
                                                                                        <div className="upload_images d-flex gap-2 align-items-start upload-static-img" key={key}>
                                                                                            <div className="img" style={{ position: "relative" }}>
                                                                                                <span className="default-img default-static-img"><img src={deepZoom} alt="" /></span>
                                                                                                <span className="icon-close" onClick={() => handleHistologyImageRemove(key)} style={{ position: "absolute", top: 0, right: 0, cursor: "pointer", background: "rgba(0,0,0,0.5)", color: "#fff", borderRadius: "50%", width: "18px", height: "18px", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", lineHeight: 1 }}>×</span>
                                                                                            </div>
                                                                                        </div>
                                                                                    ))}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* ── ASSIGN TO PATHOLOGIST ──────── */}
                                                                <div className="col-lg-12 mt-2">
                                                                    <label className="form_label">ASSIGN TO PATHOLOGIST</label>
                                                                </div>
                                                                <div className="col-lg-4 col-md-6 col-sm-12">
                                                                    <div className="mb-3 input-field">
                                                                        <select
                                                                            className="form-control form-select"
                                                                            value={selectedPathologistId}
                                                                            onChange={(e) => setSelectedPathologistId(e.target.value)}
                                                                        >
                                                                            <option value="">Select Pathologist</option>
                                                                            {pathologistList.map((p) => (
                                                                                <option key={p.userid} value={p.userid}>{p.name}</option>
                                                                            ))}
                                                                        </select>
                                                                    </div>
                                                                </div>

                                                                {/* SUBMIT */}
                                                                <div className="col-md-12 mt-3">
                                                                    <button
                                                                        className="create-quiz-btn border-0"
                                                                        type="button"
                                                                        onClick={handleSubmit}
                                                                    >
                                                                        Create
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* ════ PAYMENT TAB ════ */}
                                                        {activeTab === "payment" && (
                                                            <div>
                                                                <h5 className="mb-4" style={{ fontWeight: "600", color: "#333" }}>Payment (optional)</h5>
                                                                <p className="text-muted small mb-3">Fill this in only if payment has already been collected. It will be saved along with the case when you click Create.</p>
                                                                <div className="row col-lg-12">
                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">TEST NAME</label>
                                                                            <input type="text" className="form-control" readOnly value={defaultTestName} />
                                                                        </div>
                                                                    </div>
                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">AMOUNT (₹)</label>
                                                                            <input type="text" className="form-control" readOnly value={defaultTestCost} />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">PAYMENT MODE</label>
                                                                            <select
                                                                                className="form-control form-select"
                                                                                value={paymentMode}
                                                                                onChange={(e) => setPaymentMode(e.target.value)}
                                                                            >
                                                                                <option value="">Select Mode</option>
                                                                                <option value="Cash">Cash</option>
                                                                                <option value="UPI">UPI</option>
                                                                                <option value="Card">Card (Debit / Credit)</option>
                                                                                <option value="Net Banking">Net Banking</option>
                                                                                <option value="Cheque">Cheque</option>
                                                                                <option value="DD">Demand Draft (DD)</option>
                                                                            </select>
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">PAYMENT DATE</label>
                                                                            <input
                                                                                type="date"
                                                                                className="form-control"
                                                                                value={paymentDate}
                                                                                onChange={(e) => setPaymentDate(e.target.value)}
                                                                                max={new Date().toISOString().split("T")[0]}
                                                                            />
                                                                        </div>
                                                                    </div>

                                                                    <div className="col-lg-4 col-md-6 col-sm-12">
                                                                        <div className="mb-3 input-field">
                                                                            <label className="form_label">PAYMENT / TRANSACTION ID</label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                value={paymentId}
                                                                                onChange={(e) => setPaymentId(e.target.value)}
                                                                                placeholder="Enter receipt / transaction reference"
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                <div className="col-md-12 mt-2">
                                                                    <button
                                                                        className="create-quiz-btn border-0"
                                                                        onClick={handleSubmit}
                                                                    >
                                                                        Create
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>

            {/* Image popup lightbox */}
            {showImagePopup && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
                        backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1003,
                        display: "flex", alignItems: "start", justifyContent: "center", padding: "20px",
                    }}
                    onClick={() => setShowImagePopup(false)}
                >
                    <div
                        style={{ position: "relative", width: "auto", height: "100%", padding: "20px", display: "flex", justifyContent: "center", alignItems: "start" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img src={popupImageUrl} style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain", display: "block" }} />
                        <button
                            onClick={() => setShowImagePopup(false)}
                            style={{ position: "absolute", top: "10px", right: "10px", background: "white", border: "none", borderRadius: "50%", width: "30px", height: "30px", cursor: "pointer", fontWeight: "bold" }}
                        >×</button>
                    </div>
                </div>
            )}
        </>
    );
}
